"use strict";
// (C) 2019-2020 GoodData Corporation
Object.defineProperty(exports, "__esModule", { value: true });
exports.dimensionsFindItem = exports.newDimension = exports.newTwoDimensional = exports.dimensionSetTotals = exports.dimensionTotals = exports.isDimension = exports.MeasureGroupIdentifier = void 0;
var tslib_1 = require("tslib");
var ts_invariant_1 = tslib_1.__importDefault(require("ts-invariant"));
var totals_1 = require("./totals");
var isEmpty_1 = tslib_1.__importDefault(require("lodash/isEmpty"));
var findIndex_1 = tslib_1.__importDefault(require("lodash/findIndex"));
var attribute_1 = require("../attribute");
/**
 * Measure Group is a pseudo-identifier which can be used in an execution dimension and indicates
 * that this dimension MUST contain all the measures.
 *
 * @public
 */
exports.MeasureGroupIdentifier = "measureGroup";
var isMeasureGroupIdentifier = function (itemOrTotal) { return itemOrTotal === exports.MeasureGroupIdentifier; };
//
// Type guards
//
/**
 * Type guard checking whether object is of IDimension type.
 *
 * @public
 */
function isDimension(obj) {
    return !isEmpty_1.default(obj) && obj.itemIdentifiers !== undefined;
}
exports.isDimension = isDimension;
//
// Public functions
//
/**
 * Gets totals defined in the provided dimension
 *
 * @param dim - dimension to work with
 * @returns totals in the dimension or empty array if none
 * @public
 */
function dimensionTotals(dim) {
    ts_invariant_1.default(dim, "dimension must be specified");
    return dim.totals ? dim.totals : [];
}
exports.dimensionTotals = dimensionTotals;
/**
 * Creates a new dimension which has same items as the provided dimension but different totals.
 *
 * @param dim - dimension to inherit item identifiers from
 * @param totals - totals to have in the new dimension
 * @returns new dimension
 * @public
 */
function dimensionSetTotals(dim, totals) {
    if (totals === void 0) { totals = []; }
    ts_invariant_1.default(dim, "dimension must be specified");
    var totalsProp = !isEmpty_1.default(totals) ? { totals: totals } : {};
    return tslib_1.__assign({ itemIdentifiers: dim.itemIdentifiers }, totalsProp);
}
exports.dimensionSetTotals = dimensionSetTotals;
/**
 * Creates new two dimensional specification where each dimension will have the provided set of
 * identifiers.
 *
 * The 'measureGroup' identifier MAY be specified in only one of the dimensions.
 *
 * @param dim1Input - items to put into the first dimension, this can be item identifiers or totals
 * @param dim2Input - items to put into the second dimension, this can be item identifiers or totals
 * @returns array with exactly two dimensions
 * @public
 */
function newTwoDimensional(dim1Input, dim2Input) {
    ts_invariant_1.default(dim1Input, "input for first dimension must be specified");
    ts_invariant_1.default(dim2Input, "input for second dimension must be specified");
    var atMostOneMeasureGroup = !(dim1Input.find(isMeasureGroupIdentifier) && dim2Input.find(isMeasureGroupIdentifier));
    ts_invariant_1.default(atMostOneMeasureGroup, "The 'measureGroup' identifier must only be specified in one dimension.");
    return [newDimension(dim1Input), newDimension(dim2Input)];
}
exports.newTwoDimensional = newTwoDimensional;
/**
 * Creates new single-dimensional specification where the dimension will have the provided set of identifiers.
 *
 * @param items - allows for mix of item identifiers, attributes and total definitions to have in the new dimension
 * @param totals - additional totals to add to the dimension
 * @returns single dimension
 * @public
 */
function newDimension(items, totals) {
    var _a;
    if (items === void 0) { items = []; }
    if (totals === void 0) { totals = []; }
    var input = items.reduce(function (acc, value) {
        if (totals_1.isTotal(value)) {
            acc.totals.push(value);
        }
        else if (attribute_1.isAttribute(value)) {
            acc.ids.push(attribute_1.attributeLocalId(value));
        }
        else {
            acc.ids.push(value);
        }
        return acc;
    }, { ids: [], totals: [] });
    (_a = input.totals).push.apply(_a, totals);
    var totalsProp = !isEmpty_1.default(input.totals) ? { totals: input.totals } : {};
    return tslib_1.__assign({ itemIdentifiers: input.ids }, totalsProp);
}
exports.newDimension = newDimension;
/**
 * Looks for item with the provided local identifier among the dimensions.
 *
 * @param dims - list of dimensions to look in
 * @param localId - local identifier to find among item identifiers
 * @returns list of items in dimensions, empty if not found, may contain more than one entry if
 *  item is in multiple dimensions
 * @public
 */
function dimensionsFindItem(dims, localId) {
    var result = [];
    for (var dimIdx = 0; dimIdx < dims.length; dimIdx++) {
        var dim = dims[dimIdx];
        var itemIdx = findIndex_1.default(dim.itemIdentifiers, function (i) { return i === localId; });
        if (itemIdx >= 0) {
            result.push({ dim: dim, dimIdx: dimIdx, itemIdx: itemIdx });
        }
    }
    return result;
}
exports.dimensionsFindItem = dimensionsFindItem;
//# sourceMappingURL=dimension.js.map